import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Option,
  Select,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Typography,
} from '@mui/joy';
import { Database, Eye, FileSpreadsheet, Layers, Pencil, Save, Settings2, ShieldAlert } from 'lucide-react';

import { getAllModulesRequest } from '../../redux/actions/moduleActions';
import { getRolesByModuleRequest } from '../../redux/actions/roleActions';
import {
  createKpiConfigurationRequest,
  getAllKpiConfigurationsRequest,
  getKpiDashboardPreviewRequest,
  getKpiDashboardsMetadataRequest,
  getKpiFieldsMetadataRequest,
  getKpiSourcesMetadataRequest,
  getKpiWidgetPreviewRequest,
  updateKpiConfigurationRequest,
} from '../../redux/actions/kpiActions';
import {
  createReportConfigurationRequest,
  exportReportRequest,
  getAllReportConfigurationsRequest,
  updateReportConfigurationRequest,
} from '../../redux/actions/reportActions';
import KpiWidget from './components/KpiWidget';

const safeArray = (value) => (Array.isArray(value) ? value : []);

const normalizeFormationRole = (value) => {
  const raw = String(value || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_');

  if (
    raw.includes('RESPONSABLE_DE_FORMATION')
    || raw.includes('RESPONSABLE_FORMATION')
    || raw === 'RF'
    || raw.endsWith('_RF')
    || raw === 'ROLE_RF'
  ) {
    return 'RESPONSABLE_FORMATION';
  }
  if (raw.includes('MANAGER')) return 'MANAGER';
  if (
    raw.includes('APPRENANT')
    || raw.includes('COLLABORATEUR')
    || raw.includes('COLLABORATOR')
    || raw === 'ROLE_USER'
    || raw === 'USER'
  ) {
    return 'COLLABORATOR';
  }
  if (raw.includes('RH')) return 'RESPONSABLE_RH';
  return raw;
};

const emptyKpiForm = {
  id: null,
  code: '',
  name: '',
  description: '',
  moduleKey: '',
  roleKey: '',
  dashboardKey: '',
  sourceKey: '',
  fieldKey: '',
  aggregationType: 'COUNT',
  visualizationType: 'CARD',
  formatType: 'NUMBER',
  groupByFieldKey: '',
  posX: 0,
  posY: 0,
  width: 4,
  height: 2,
  displayOrder: 0,
  active: true,
  published: true,
  filters: {},
};

const emptyReportForm = {
  id: null,
  code: '',
  name: '',
  description: '',
  moduleKey: '',
  roleKey: '',
  dashboardKey: '',
  reportKey: '',
  sourceKey: '',
  selectedColumns: [],
  supportedFilters: ['campaignId', 'dateFrom', 'dateTo', 'globalSearch'],
  filters: {},
  displayOrder: 0,
  active: true,
};

const parseNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const shellCardSx = {
  borderRadius: '14px',
  borderColor: '#e2e8f0',
  boxShadow: '0 8px 20px rgba(15,23,42,0.06)',
  backgroundColor: '#fff',
};

const actionButtonSx = {
  borderRadius: '12px',
  fontWeight: 600,
};

const buildRoleOptions = (roles) => {
  const base = safeArray(roles).map((role) => {
    const value = role?.code || role?.key || role?.roleName || role?.name || '';
    return {
      value,
      normalized: normalizeFormationRole(value),
      label: role?.label || role?.name || role?.roleName || value,
    };
  }).filter((item) => item.value);

  const fallbackFormation = [
    { value: 'RESPONSABLE_FORMATION', normalized: 'RESPONSABLE_FORMATION', label: 'Responsable Formation' },
    { value: 'RF', normalized: 'RESPONSABLE_FORMATION', label: 'RF' },
    { value: 'MANAGER', normalized: 'MANAGER', label: 'Manager' },
    { value: 'APPRENANT', normalized: 'COLLABORATOR', label: 'Apprenant' },
    { value: 'COLLABORATEUR', normalized: 'COLLABORATOR', label: 'Collaborateur' },
  ];

  const byNormalized = new Map();
  [...base, ...fallbackFormation].forEach((item) => {
    if (!byNormalized.has(item.normalized)) {
      byNormalized.set(item.normalized, item);
    }
  });

  return [...byNormalized.values()];
};

const getDashboardHint = (moduleKey, normalizedRole, dashboards) => {
  if (!moduleKey) return 'Selectionnez d abord un module.';
  if (!normalizedRole) return 'Selectionnez un role pour charger les dashboards.';
  if (!safeArray(dashboards).length) return 'Aucun dashboard disponible pour ce role.';
  return '';
};

const SectionTitle = ({ title, helper }) => (
  <Box>
    <Typography level="title-md" sx={{ color: '#0f172a' }}>{title}</Typography>
    {helper ? <Typography level="body-xs" sx={{ color: '#64748b', mt: 0.35 }}>{helper}</Typography> : null}
  </Box>
);

const InfoEmpty = ({ text }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: '12px',
      backgroundColor: '#f8fafc',
      border: '1px dashed #cbd5e1',
      color: '#64748b',
      fontSize: 13,
    }}
  >
    {text}
  </Box>
);

export default function KpiManagement() {
  const dispatch = useDispatch();
  const modulesState = useSelector((state) => state.module || {});
  const rolesState = useSelector((state) => state.role || {});
  const kpiState = useSelector((state) => state.kpi || {});
  const reportsState = useSelector((state) => state.reports || {});

  const modules = safeArray(modulesState.modules);
  const roles = safeArray(rolesState.roles);
  const roleOptions = useMemo(() => buildRoleOptions(roles), [roles]);
  const kpiConfigurations = safeArray(kpiState?.configurations?.list);
  const reportConfigurations = safeArray(reportsState?.list);
  const dashboardPreviewWidgets = safeArray(kpiState?.preview?.dashboardPreview?.widgets);
  const widgetPreview = kpiState?.preview?.widgetPreview || null;
  const metadata = kpiState?.metadata || {};
  const kpiLoading = kpiState?.loading || {};

  const [tab, setTab] = useState(0);
  const [kpiForm, setKpiForm] = useState(emptyKpiForm);
  const [reportForm, setReportForm] = useState(emptyReportForm);

  useEffect(() => {
    dispatch(getAllModulesRequest());
    dispatch(getAllKpiConfigurationsRequest());
    dispatch(getAllReportConfigurationsRequest());
  }, [dispatch]);

  useEffect(() => {
    const form = tab === 0 ? kpiForm : reportForm;
    if (!form.moduleKey) return;
    const selectedModule = modules.find((module) => module.code === form.moduleKey || module.key === form.moduleKey);
    if (selectedModule?.id) {
      dispatch(getRolesByModuleRequest(selectedModule.id));
    }
    dispatch(getKpiSourcesMetadataRequest(form.moduleKey));
  }, [dispatch, modules, kpiForm, reportForm, tab]);

  useEffect(() => {
    if (tab !== 0) return;
    if (kpiForm.moduleKey && kpiForm.roleKey) {
      dispatch(getKpiDashboardsMetadataRequest(kpiForm.moduleKey, normalizeFormationRole(kpiForm.roleKey)));
    }
  }, [dispatch, kpiForm.moduleKey, kpiForm.roleKey, tab]);

  useEffect(() => {
    if (tab !== 1) return;
    if (reportForm.moduleKey && reportForm.roleKey) {
      dispatch(getKpiDashboardsMetadataRequest(reportForm.moduleKey, normalizeFormationRole(reportForm.roleKey)));
    }
  }, [dispatch, reportForm.moduleKey, reportForm.roleKey, tab]);

  useEffect(() => {
    if (tab !== 0) return;
    if (kpiForm.sourceKey) {
      dispatch(getKpiFieldsMetadataRequest(kpiForm.sourceKey));
    }
  }, [dispatch, kpiForm.sourceKey, tab]);

  useEffect(() => {
    if (tab !== 1) return;
    if (reportForm.sourceKey) {
      dispatch(getKpiFieldsMetadataRequest(reportForm.sourceKey));
    }
  }, [dispatch, reportForm.sourceKey, tab]);

  const dashboardOptions = useMemo(() => safeArray(metadata?.dashboards), [metadata]);
  const sourceOptions = useMemo(() => safeArray(metadata?.sources), [metadata]);
  const fieldOptions = useMemo(() => safeArray(metadata?.fields), [metadata]);
  const groupedFieldOptions = useMemo(() => fieldOptions.filter((field) => field?.groupable), [fieldOptions]);

  const normalizedKpiRole = normalizeFormationRole(kpiForm.roleKey);
  const normalizedReportRole = normalizeFormationRole(reportForm.roleKey);
  const isLearnerReportRole = reportForm.moduleKey === 'FORMATION' && normalizedReportRole === 'COLLABORATOR';

  useEffect(() => {
    if (!kpiForm.dashboardKey && dashboardOptions.length === 1) {
      setKpiForm((prev) => ({ ...prev, dashboardKey: dashboardOptions[0].key }));
    }
  }, [dashboardOptions, kpiForm.dashboardKey]);

  useEffect(() => {
    if (isLearnerReportRole) {
      setReportForm((prev) => ({ ...prev, dashboardKey: '' }));
      return;
    }
    if (!reportForm.dashboardKey && dashboardOptions.length === 1) {
      setReportForm((prev) => ({ ...prev, dashboardKey: dashboardOptions[0].key }));
    }
  }, [dashboardOptions, isLearnerReportRole, reportForm.dashboardKey]);

  useEffect(() => {
    if (!kpiForm.sourceKey || !fieldOptions.length) return;
    setKpiForm((prev) => {
      const nextFieldKey = prev.fieldKey && fieldOptions.some((field) => field.fieldKey === prev.fieldKey)
        ? prev.fieldKey
        : (fieldOptions.find((field) => field.aggregatable)?.fieldKey || fieldOptions[0]?.fieldKey || '');
      const nextGroupBy = prev.groupByFieldKey && groupedFieldOptions.some((field) => field.fieldKey === prev.groupByFieldKey)
        ? prev.groupByFieldKey
        : '';
      return { ...prev, fieldKey: nextFieldKey, groupByFieldKey: nextGroupBy };
    });
  }, [fieldOptions, groupedFieldOptions, kpiForm.sourceKey]);

  useEffect(() => {
    if (!reportForm.sourceKey || !fieldOptions.length) return;
    setReportForm((prev) => {
      const existing = safeArray(prev.selectedColumns).filter((fieldKey) => fieldOptions.some((field) => field.fieldKey === fieldKey));
      return {
        ...prev,
        selectedColumns: existing.length ? existing : fieldOptions.map((field) => field.fieldKey),
      };
    });
  }, [fieldOptions, reportForm.sourceKey]);

  const saveKpi = () => {
    const payload = {
      ...kpiForm,
      roleKey: normalizeFormationRole(kpiForm.roleKey),
    };
    if (payload.id) {
      dispatch(updateKpiConfigurationRequest(payload.id, payload));
    } else {
      dispatch(createKpiConfigurationRequest(payload));
    }
  };

  const saveReport = () => {
    if (isLearnerReportRole) return;
    const payload = {
      ...reportForm,
      roleKey: normalizeFormationRole(reportForm.roleKey),
      selectedColumns: safeArray(reportForm.selectedColumns),
      reportKey: reportForm.reportKey || reportForm.code,
    };
    if (payload.id) {
      dispatch(updateReportConfigurationRequest(payload.id, payload));
    } else {
      dispatch(createReportConfigurationRequest(payload));
    }
  };

  const canPreviewWidget = Boolean(kpiForm.moduleKey && kpiForm.roleKey && kpiForm.sourceKey);
  const canPreviewDashboard = Boolean(kpiForm.dashboardKey && kpiForm.sourceKey);

  const renderConfigurationCard = (item, type) => (
    <Card key={`${type}-${item?.id}`} variant="outlined" sx={{ ...shellCardSx, p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
        <Box>
          <Typography level="title-sm" sx={{ color: '#0f172a' }}>{item?.name || '-'}</Typography>
          <Typography level="body-xs" sx={{ color: '#64748b', mt: 0.5 }}>
            {item?.dashboardKey || '-'} | {item?.sourceKey || '-'} | ordre {item?.displayOrder ?? 0}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip size="sm" variant="soft" sx={{ backgroundColor: item?.active ? 'rgba(32,67,133,0.1)' : 'rgba(148,163,184,0.16)', color: item?.active ? '#2f4595' : '#64748b' }}>
              {item?.active ? 'Actif' : 'Inactif'}
            </Chip>
            <Chip size="sm" variant="soft" sx={{ backgroundColor: '#f8fafc', color: '#475569' }}>
              {item?.moduleKey || '-'}
            </Chip>
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            size="sm"
            variant="solid"
            startDecorator={<Pencil size={14} />}
            sx={{ ...actionButtonSx, backgroundColor: '#2f4595', '&:hover': { backgroundColor: '#334785' } }}
            onClick={() => {
              if (type === 'kpi') {
                setKpiForm({ ...emptyKpiForm, ...item });
                setTab(0);
              } else {
                setReportForm({ ...emptyReportForm, ...item, selectedColumns: safeArray(item?.selectedColumns) });
                setTab(1);
              }
            }}
          >
            Modifier
          </Button>
          {type === 'report' ? (
            <Button
              size="sm"
              variant="soft"
              startDecorator={<FileSpreadsheet size={14} />}
              onClick={() => dispatch(exportReportRequest(item.id, {}))}
            >
              Export
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="soft"
            color="danger"
            sx={{ ...actionButtonSx, color: '#ef4444' }}
            onClick={() => {
              const payload = { ...item, active: !item?.active };
              if (type === 'kpi') {
                dispatch(updateKpiConfigurationRequest(item.id, payload));
              } else {
                dispatch(updateReportConfigurationRequest(item.id, payload));
              }
            }}
          >
            {item?.active ? 'Desactiver' : 'Activer'}
          </Button>
        </Stack>
      </Stack>
    </Card>
  );

  const renderSelectBlock = ({
    label,
    value,
    onChange,
    placeholder,
    options,
    getValue,
    getLabel,
    disabled,
    helper,
  }) => (
    <FormControl disabled={disabled}>
      <FormLabel>{label}</FormLabel>
      <Select value={value || null} onChange={(event, nextValue) => onChange(nextValue || '')} placeholder={placeholder}>
        {safeArray(options).map((option, index) => (
          <Option key={`${getValue(option)}-${index}`} value={getValue(option)}>
            {getLabel(option)}
          </Option>
        ))}
      </Select>
      {helper ? <FormHelperText>{helper}</FormHelperText> : null}
    </FormControl>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1540, mx: 'auto', background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)', minHeight: '100%' }}>
      <Stack spacing={2.5}>
        <Box sx={{ px: 0.5 }}>
          <Typography level="h2" startDecorator={<Settings2 size={24} />} sx={{ color: '#0f172a' }}>
            Kpi Management
          </Typography>

        </Box>

        <Tabs value={tab} onChange={(event, value) => setTab(value)} sx={{ '--Tabs-gap': '12px' }}>
          <TabList sx={{ p: 0.75, borderRadius: '14px', backgroundColor: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 8px 20px rgba(15,23,42,0.06)', gap: 1 }}>
            <Tab value={0} sx={{ borderRadius: '12px', fontWeight: 600, '&[aria-selected="true"]': { backgroundColor: '#2f4595', color: '#fff' } }}>
              KPIs
            </Tab>
            <Tab value={1} sx={{ borderRadius: '12px', fontWeight: 600, '&[aria-selected="true"]': { backgroundColor: '#2f4595', color: '#fff' } }}>
              Etats Excel
            </Tab>
          </TabList>

          <TabPanel value={0} sx={{ px: 0, pt: 2 }}>
            <Grid container spacing={2.5}>
              <Grid xs={12} lg={7}>
                <Card variant="outlined" sx={{ ...shellCardSx, p: 2.5 }}>
                  <Stack spacing={2.25}>
                    <SectionTitle title={kpiForm.id ? 'Modifier un KPI' : 'Créer un KPI'} helper="Structurée en sections pour charger module, role, dashboard, source, calcul et preview." />

                    <SectionTitle title="1. Contexte" />
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        {renderSelectBlock({
                          label: 'Module',
                          value: kpiForm.moduleKey,
                          onChange: (value) => setKpiForm((prev) => ({ ...prev, moduleKey: value, roleKey: '', dashboardKey: '', sourceKey: '', fieldKey: '', groupByFieldKey: '' })),
                          placeholder: modulesState.loading ? 'Chargement...' : 'Selectionner un module',
                          options: modules,
                          getValue: (module) => module?.code || module?.key,
                          getLabel: (module) => module?.label || module?.nom || module?.name || module?.code,
                          helper: '',
                        })}
                      </Grid>
                      <Grid xs={12} md={6}>
                        {renderSelectBlock({
                          label: 'Role cible',
                          value: kpiForm.roleKey,
                          onChange: (value) => setKpiForm((prev) => ({ ...prev, roleKey: value, dashboardKey: '' })),
                          placeholder: rolesState.loading ? 'Chargement...' : 'Selectionner un role',
                          options: roleOptions,
                          getValue: (role) => role?.value,
                          getLabel: (role) => role?.label,
                          disabled: !kpiForm.moduleKey,
                          helper: !kpiForm.moduleKey ? 'Selectionnez un module avant le role.' : 'Les alias RF / Manager / Apprenant / Collaborateur sont tous normalises.',
                        })}
                      </Grid>
                    </Grid>

                    <SectionTitle title="2. Source de données" />
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        {renderSelectBlock({
                          label: 'Dashboard de destination',
                          value: kpiForm.dashboardKey,
                          onChange: (value) => setKpiForm((prev) => ({ ...prev, dashboardKey: value })),
                          placeholder: dashboardOptions.length ? 'Selectionner un dashboard' : 'Aucun dashboard',
                          options: dashboardOptions,
                          getValue: (dashboard) => dashboard?.key,
                          getLabel: (dashboard) => dashboard?.label,
                          disabled: !kpiForm.roleKey || !dashboardOptions.length,
                          helper: getDashboardHint(kpiForm.moduleKey, normalizedKpiRole, dashboardOptions),
                        })}
                      </Grid>
                      <Grid xs={12} md={6}>
                        {renderSelectBlock({
                          label: 'Source',
                          value: kpiForm.sourceKey,
                          onChange: (value) => setKpiForm((prev) => ({ ...prev, sourceKey: value, fieldKey: '', groupByFieldKey: '' })),
                          placeholder: sourceOptions.length ? 'Selectionner une source' : 'Aucune source',
                          options: sourceOptions,
                          getValue: (source) => source?.key,
                          getLabel: (source) => source?.label,
                          disabled: !kpiForm.moduleKey,
                          helper: !kpiForm.moduleKey ? 'La source se charge aprés sélection du module.' : '',
                        })}
                      </Grid>
                    </Grid>

                    <SectionTitle title="3. Calcul / agrégation" />
                    <Grid container spacing={2}>
                      <Grid xs={12} md={4}>
                        {renderSelectBlock({
                          label: 'Champ principal',
                          value: kpiForm.fieldKey,
                          onChange: (value) => setKpiForm((prev) => {
                            const field = fieldOptions.find((item) => item?.fieldKey === value);
                            return {
                              ...prev,
                              fieldKey: value,
                              aggregationType: field?.dataType === 'DURATION' ? 'AVG' : prev.aggregationType,
                              formatType: field?.dataType === 'DURATION' ? 'DURATION' : prev.formatType,
                            };
                          }),
                          placeholder: fieldOptions.length ? 'Selectionner un champ' : 'Aucun champ',
                          options: fieldOptions,
                          getValue: (field) => field?.fieldKey,
                          getLabel: (field) => field?.displayName,
                          disabled: !kpiForm.sourceKey || !fieldOptions.length,
                          helper: !kpiForm.sourceKey ? 'Selectionnez une source avant le champ principal.' : '',
                        })}
                      </Grid>
                      <Grid xs={12} md={4}>
                        <FormControl>
                          <FormLabel>Type d agregation</FormLabel>
                          <Select value={kpiForm.aggregationType || null} onChange={(event, value) => setKpiForm((prev) => ({ ...prev, aggregationType: value || 'COUNT' }))}>
                            <Option value="COUNT">COUNT</Option>
                            <Option value="SUM">SUM</Option>
                            <Option value="AVG">AVG</Option>
                            <Option value="PERCENTAGE">PERCENTAGE</Option>
                            <Option value="GROUPED_COUNT">GROUPED_COUNT</Option>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={4}>
                        {renderSelectBlock({
                          label: 'Group By',
                          value: kpiForm.groupByFieldKey,
                          onChange: (value) => setKpiForm((prev) => ({ ...prev, groupByFieldKey: value })),
                          placeholder: groupedFieldOptions.length ? 'Optionnel' : 'Aucun group by',
                          options: groupedFieldOptions,
                          getValue: (field) => field?.fieldKey,
                          getLabel: (field) => field?.displayName,
                          disabled: !kpiForm.sourceKey || !groupedFieldOptions.length,
                          helper: !kpiForm.sourceKey ? 'Le group by depend de la source choisie.' : '',
                        })}
                      </Grid>
                    </Grid>

                    <SectionTitle title="4. Affichage / layout" />
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Nom KPI</FormLabel>
                          <Input value={kpiForm.name} onChange={(event) => setKpiForm((prev) => ({ ...prev, name: event.target.value }))} />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Code KPI</FormLabel>
                          <Input value={kpiForm.code} onChange={(event) => setKpiForm((prev) => ({ ...prev, code: event.target.value }))} />
                        </FormControl>
                      </Grid>
                      <Grid xs={12}>
                        <FormControl>
                          <FormLabel>Description</FormLabel>
                          <Input value={kpiForm.description} onChange={(event) => setKpiForm((prev) => ({ ...prev, description: event.target.value }))} />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={4}>
                        <FormControl>
                          <FormLabel>Visualisation</FormLabel>
                          <Select value={kpiForm.visualizationType || null} onChange={(event, value) => setKpiForm((prev) => ({ ...prev, visualizationType: value || 'CARD' }))}>
                            <Option value="CARD">CARD</Option>
                            <Option value="BAR">BAR</Option>
                            <Option value="LINE">LINE</Option>
                            <Option value="PIE">PIE</Option>
                            <Option value="TABLE">TABLE</Option>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={4}>
                        <FormControl>
                          <FormLabel>Largeur</FormLabel>
                          <Input type="number" value={kpiForm.width} onChange={(event) => setKpiForm((prev) => ({ ...prev, width: parseNumber(event.target.value, 4) }))} />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={4}>
                        <FormControl>
                          <FormLabel>Ordre</FormLabel>
                          <Input type="number" value={kpiForm.displayOrder} onChange={(event) => setKpiForm((prev) => ({ ...prev, displayOrder: parseNumber(event.target.value, 0) }))} />
                        </FormControl>
                      </Grid>
                    </Grid>

                    <SectionTitle title="5. Preview" helper="La preview dashboard combine les widgets existants avec le widget simule non sauvegarde." />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} useFlexGap>
                      <Button
                        startDecorator={<Eye size={16} />}
                        loading={kpiLoading.previewLoading}
                        disabled={!canPreviewWidget}
                        sx={{ ...actionButtonSx, backgroundColor: '#2f4595', '&:hover': { backgroundColor: '#334785' } }}
                        onClick={() => dispatch(getKpiWidgetPreviewRequest({ ...kpiForm, roleKey: normalizeFormationRole(kpiForm.roleKey) }))}
                      >
                        Preview widget
                      </Button>
                      <Button
                        startDecorator={<Layers size={16} />}
                        variant="soft"
                        loading={kpiLoading.previewLoading}
                        disabled={!canPreviewDashboard}
                        sx={actionButtonSx}
                        onClick={() => dispatch(getKpiDashboardPreviewRequest(kpiForm.dashboardKey, { ...kpiForm, roleKey: normalizeFormationRole(kpiForm.roleKey) }))}
                      >
                        Preview dashboard
                      </Button>
                      <Button startDecorator={<Save size={16} />} color="success" loading={kpiLoading.saveLoading} sx={actionButtonSx} onClick={saveKpi}>
                        {kpiForm.id ? 'Mettre a jour' : 'Enregistrer'}
                      </Button>
                      <Button variant="soft" color="neutral" sx={actionButtonSx} onClick={() => setKpiForm({ ...emptyKpiForm })}>
                        Nouveau
                      </Button>
                    </Stack>

                    {widgetPreview ? (
                      <Card variant="outlined" sx={{ ...shellCardSx, p: 1.5, backgroundColor: '#f8fafc' }}>
                        <Typography level="body-sm" sx={{ mb: 1, color: '#475569' }}>Apercu widget</Typography>
                        <KpiWidget configuration={widgetPreview.configuration} data={widgetPreview.data} isSimulated />
                      </Card>
                    ) : (
                      <InfoEmpty text="La preview widget s'affichera ici aprés une simulation valide." />
                    )}

                    <Card variant="outlined" sx={{ ...shellCardSx, p: 1.5, backgroundColor: '#f8fafc' }}>
                      <Typography level="body-sm" sx={{ mb: 1.25, color: '#475569' }}>Aperçu dashboard</Typography>
                      {dashboardPreviewWidgets.length ? (
                        <Grid container spacing={1.5}>
                          {dashboardPreviewWidgets.map((widget, index) => (
                            <Grid xs={12} md={widget?.configuration?.width || 4} key={widget?.configuration?.id || `${widget?.configuration?.code || 'widget'}-${index}`}>
                              <KpiWidget configuration={widget?.configuration} data={widget?.data} isSimulated={Boolean(widget?.isSimulated)} />
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <InfoEmpty text="Aucun widget à afficher pour le moment. Lancez la preview dashboard pour voir les widgets existants et la simulation courante." />
                      )}
                    </Card>
                  </Stack>
                </Card>
              </Grid>

              <Grid xs={12} lg={5}>
                <Card variant="outlined" sx={{ ...shellCardSx, p: 2.5 }}>
                  <Stack spacing={1.5}>
                    <SectionTitle title="Configurations KPI" helper="Cards compactes avec dashboard, source, ordre et statut." />
                    {kpiConfigurations.length ? kpiConfigurations.map((item) => renderConfigurationCard(item, 'kpi')) : <InfoEmpty text="Aucune configuration KPI disponible pour le moment." />}
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={1} sx={{ px: 0, pt: 2 }}>
            <Grid container spacing={2.5}>
              <Grid xs={12} lg={7}>
                <Card variant="outlined" sx={{ ...shellCardSx, p: 2.5 }}>
                  <Stack spacing={2.25}>
                    <SectionTitle title={reportForm.id ? 'Modifier un Etat Excel' : 'Créer un Etat Excel'} helper="Les etats Excel ne sont autorisés que pour RF et Manager sur Formation." />

                    <SectionTitle title="1. Contexte" />
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        {renderSelectBlock({
                          label: 'Module',
                          value: reportForm.moduleKey,
                          onChange: (value) => setReportForm((prev) => ({ ...prev, moduleKey: value, roleKey: '', dashboardKey: '', sourceKey: '', selectedColumns: [] })),
                          placeholder: modulesState.loading ? 'Chargement...' : 'Selectionner un module',
                          options: modules,
                          getValue: (module) => module?.code || module?.key,
                          getLabel: (module) => module?.label || module?.nom || module?.name || module?.code,
                          helper: 'Le module charge les roles et les sources disponibles.',
                        })}
                      </Grid>
                      <Grid xs={12} md={6}>
                        {renderSelectBlock({
                          label: 'Role cible',
                          value: reportForm.roleKey,
                          onChange: (value) => setReportForm((prev) => ({ ...prev, roleKey: value, dashboardKey: '' })),
                          placeholder: rolesState.loading ? 'Chargement...' : 'Selectionner un role',
                          options: roleOptions,
                          getValue: (role) => role?.value,
                          getLabel: (role) => role?.label,
                          disabled: !reportForm.moduleKey,
                          helper: !reportForm.moduleKey ? 'Selectionnez un module avant le role.' : 'Le role Apprenant/Collaborateur ne propose pas d Etat Excel.',
                        })}
                      </Grid>
                    </Grid>

                    {isLearnerReportRole ? (
                      <Card variant="soft" color="warning" sx={{ borderRadius: '12px', p: 1.5 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ShieldAlert size={16} />
                          <Typography level="body-sm">
                            Le dashboard Formation Apprenant n expose pas l onglet Etats Excel. Selectionnez RF ou Manager pour configurer un export.
                          </Typography>
                        </Stack>
                      </Card>
                    ) : null}

                    <SectionTitle title="2. Source de données" />
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        {renderSelectBlock({
                          label: 'Dashboard de destination',
                          value: reportForm.dashboardKey,
                          onChange: (value) => setReportForm((prev) => ({ ...prev, dashboardKey: value })),
                          placeholder: dashboardOptions.length ? 'Selectionner un dashboard' : 'Aucun dashboard',
                          options: dashboardOptions,
                          getValue: (dashboard) => dashboard?.key,
                          getLabel: (dashboard) => dashboard?.label,
                          disabled: isLearnerReportRole || !reportForm.roleKey || !dashboardOptions.length,
                          helper: isLearnerReportRole ? 'Aucun dashboard exportable pour Apprenant.' : getDashboardHint(reportForm.moduleKey, normalizedReportRole, dashboardOptions),
                        })}
                      </Grid>
                      <Grid xs={12} md={6}>
                        {renderSelectBlock({
                          label: 'Source',
                          value: reportForm.sourceKey,
                          onChange: (value) => setReportForm((prev) => ({ ...prev, sourceKey: value, selectedColumns: [] })),
                          placeholder: sourceOptions.length ? 'Selectionner une source' : 'Aucune source',
                          options: sourceOptions,
                          getValue: (source) => source?.key,
                          getLabel: (source) => source?.label,
                          disabled: isLearnerReportRole || !reportForm.moduleKey,
                          helper: !reportForm.moduleKey ? 'La source se charge apres selection du module.' : '',
                        })}
                      </Grid>
                    </Grid>

                    <SectionTitle title="3. Parametrage de l etat" />
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Nom de l etat</FormLabel>
                          <Input value={reportForm.name} onChange={(event) => setReportForm((prev) => ({ ...prev, name: event.target.value }))} />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Code</FormLabel>
                          <Input value={reportForm.code} onChange={(event) => setReportForm((prev) => ({ ...prev, code: event.target.value, reportKey: event.target.value }))} />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={4}>
                        <FormControl>
                          <FormLabel>Ordre</FormLabel>
                          <Input type="number" value={reportForm.displayOrder} onChange={(event) => setReportForm((prev) => ({ ...prev, displayOrder: parseNumber(event.target.value, 0) }))} />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={8}>
                        <FormControl>
                          <FormLabel>Description</FormLabel>
                          <Input value={reportForm.description} onChange={(event) => setReportForm((prev) => ({ ...prev, description: event.target.value }))} />
                        </FormControl>
                      </Grid>
                    </Grid>

                    <SectionTitle title="4. Colonnes visibles" helper="Les colonnes proviennent directement de la source choisie et restent toujours un tableau [] cote Redux/API." />
                    <Card variant="outlined" sx={{ ...shellCardSx, p: 1.5, backgroundColor: '#f8fafc' }}>
                      {fieldOptions.length ? (
                        <Grid container spacing={1}>
                          {fieldOptions.map((field) => (
                            <Grid xs={12} md={6} key={field?.fieldKey}>
                              <Checkbox
                                label={field?.displayName}
                                checked={safeArray(reportForm.selectedColumns).includes(field?.fieldKey)}
                                onChange={(event) => {
                                  setReportForm((prev) => ({
                                    ...prev,
                                    selectedColumns: event.target.checked
                                      ? [...new Set([...safeArray(prev.selectedColumns), field.fieldKey])]
                                      : safeArray(prev.selectedColumns).filter((item) => item !== field.fieldKey),
                                  }));
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <InfoEmpty text={!reportForm.sourceKey ? 'Selectionnez une source pour charger les colonnes visibles.' : 'Aucune colonne disponible pour cette source.'} />
                      )}
                    </Card>

                    <SectionTitle title="5. Actions" />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} useFlexGap>
                      <Button
                        startDecorator={<Save size={16} />}
                        color="success"
                        loading={reportsState?.reportSaveLoading}
                        disabled={isLearnerReportRole}
                        sx={actionButtonSx}
                        onClick={saveReport}
                      >
                        {reportForm.id ? 'Mettre a jour' : 'Enregistrer'}
                      </Button>
                      {reportForm.id ? (
                        <Button
                          startDecorator={<FileSpreadsheet size={16} />}
                          variant="soft"
                          loading={reportsState?.reportExportLoading}
                          sx={actionButtonSx}
                          onClick={() => dispatch(exportReportRequest(reportForm.id, {}))}
                        >
                          Export test
                        </Button>
                      ) : null}
                      <Button variant="soft" color="neutral" sx={actionButtonSx} onClick={() => setReportForm({ ...emptyReportForm })}>
                        Nouveau
                      </Button>
                    </Stack>
                  </Stack>
                </Card>
              </Grid>

              <Grid xs={12} lg={5}>
                <Card variant="outlined" sx={{ ...shellCardSx, p: 2.5 }}>
                  <Stack spacing={1.5}>
                    <SectionTitle title="Configurations Etats Excel" helper="Sans spinner infini: liste, empty state, edition et export de test." />
                    {reportConfigurations.length ? reportConfigurations.map((item) => renderConfigurationCard(item, 'report')) : <InfoEmpty text="Aucune configuration d Etat Excel disponible pour le moment." />}
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Tabs>
      </Stack>
    </Box>
  );
}
